const IMAGE_WAIT_MS = 3000;

/**
 * Print a document through the browser's own print dialog (printer or "Save
 * as PDF"), laid out as the document itself - not the dashboard around the
 * editor.
 *
 * The document is written into a hidden, sandboxed iframe (no allow-scripts:
 * it is only laid out and printed, never executed), so none of the app's
 * global CSS reaches it. Page breaks written as `page-break-before` on a block
 * are honoured by the browser as they stand.
 *
 * The order sheet has its own, order-specific printout (printOrderSheet.js:
 * letterhead pagination, running case number, start page). This is for
 * notices and factsheets.
 *
 * @param {object} options
 * @param {string} options.html a complete HTML document, or a body fragment
 * @param {string} [options.css] styles for a body fragment
 * @param {string} [options.extraCss] styles added on top, for a complete
 *   document as well as a fragment
 * @param {string} [options.title] the print job / PDF file name
 * @returns {Promise<void>} resolves once the print dialog has been opened
 */

// Chrome prints its own date, title, URL and page number in any page-margin
// area the page leaves empty. Giving every margin box content - an empty
// string - keeps them off the printout, whatever the print dialog's
// "Headers and footers" box says. Same approach as printOrderSheet.js.
const NO_BROWSER_HEADERS = `
  @page {
    @top-left { content: ""; }
    @top-center { content: ""; }
    @top-right { content: ""; }
    @bottom-left { content: ""; }
    @bottom-center { content: ""; }
    @bottom-right { content: ""; }
  }
`;

const escapeHtml = (text) =>
  String(text).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));

const isFullDocument = (html) => /<html[\s>]/i.test(html) || /<!doctype/i.test(html);

const waitForImages = (doc) =>
  Promise.all(
    Array.from(doc.images)
      .filter((img) => !img.complete)
      .map(
        (img) =>
          new Promise((resolve) => {
            img.addEventListener("load", resolve, { once: true });
            img.addEventListener("error", resolve, { once: true });
            setTimeout(resolve, IMAGE_WAIT_MS);
          })
      )
  );

export default async function printDocument({
  html,
  css = "",
  extraCss = "",
  title = "Document",
}) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("sandbox", "allow-same-origin allow-modals");
  Object.assign(iframe.style, {
    position: "fixed",
    right: "0",
    bottom: "0",
    width: "0",
    height: "0",
    border: "0",
    visibility: "hidden",
  });
  document.body.appendChild(iframe);
  const remove = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  try {
    const doc = iframe.contentDocument;
    doc.open();
    if (isFullDocument(html)) {
      doc.write(html);
    } else {
      doc.write(
        `<!doctype html><html><head><meta charset="utf-8">` +
          `<title>${escapeHtml(title)}</title><style>${css}</style>` +
          `</head><body>${html || ""}</body></html>`
      );
    }
    doc.close();
    if (title && !doc.title) doc.title = title;
    // Last in the head, so it applies on top of the document's own styles.
    const style = doc.createElement("style");
    style.textContent = NO_BROWSER_HEADERS + extraCss;
    (doc.head || doc.documentElement).appendChild(style);

    await waitForImages(doc);

    const win = iframe.contentWindow;
    // Chrome blocks in print() until the dialog closes; Firefox returns at
    // once and fires afterprint later. Clean up on whichever comes last.
    win.addEventListener("afterprint", () => setTimeout(remove, 0), { once: true });
    win.focus();
    win.print();
    setTimeout(remove, 60000);
  } catch (err) {
    remove();
    throw err;
  }
}
